/**
 * Stage 4, the heart of it: every field of the import, where its value comes
 * from, its steps, and what it becomes on real rows.
 */

import { useState } from "react";
import { ChevronLeft, ChevronRight, Sparkles, TriangleAlert } from "lucide-react";

import {
  Banner,
  Button,
  Column,
  FieldGroupRow,
  FieldMapRow,
  Inline,
  Meter,
  Popover,
  StepChips,
  StepEditor,
  Table,
  Textarea,
  Th,
  TonePill,
  Toolbar,
  ValueMap,
} from "@/components/kit";
import { groupCount, groupFields, labelInGroup } from "@/lib/integrations/fieldGroups";
import {
  getPreview,
  getSuggestions,
  sendChat,
  type FieldsInfo,
  type ImportFieldInfo,
  type Preview,
  type PreviewRow,
  type Sample,
} from "@/lib/integrations/api";
import {
  isEmptyValue,
  isMapped,
  readPath,
  showValue,
  unmappedValues,
  valueForType,
  valuesSeen,
  type FieldSetting,
  type Settings,
  type Step,
} from "@/lib/integrations/settings";
import { when } from "@/lib/integrations/words";
import { errorText } from "@/spa/useApi";

import { useDebounced, useKeyed } from "../hooks";

/** A field's value on a mapped row. List fields (`events[].code`) read every entry. */
function fieldValue(value: unknown, key: string): unknown {
  if (!key.includes("[]")) return readPath(value, key);
  const [head, tail] = key.split("[]");
  const list = readPath(value, head.replace(/\.$/, ""));
  if (!Array.isArray(list)) return undefined;
  const path = tail.replace(/^\./, "");
  return list.map((item) => (path ? readPath(item, path) : item));
}

function problemFor(row: PreviewRow | undefined, label: string): string | null {
  if (!row) return null;
  const prefix = `${label}:`;
  if (row.hold?.startsWith(prefix)) return row.hold.slice(prefix.length).trim();
  const found = row.problems.find((problem) => problem.startsWith(prefix));
  return found ? found.slice(prefix.length).trim() : null;
}

function withSteps(settings: Settings, key: string, steps: Step[]): Settings {
  const current = settings.fields[key];
  if (!current) return settings;
  return { ...settings, fields: { ...settings.fields, [key]: { ...current, steps } } };
}

function StepEditorFor({
  code,
  settings,
  field,
  index,
  run,
  onChange,
  onClose,
  disabled,
}: {
  code: string;
  settings: Settings;
  field: ImportFieldInfo;
  index: number;
  run: string | null;
  onChange: (step: Step) => void;
  onClose: () => void;
  disabled: boolean;
}) {
  const setting = settings.fields[field.key];
  const step = setting.steps[index];
  const upTo = useDebounced(JSON.stringify([setting.from, setting.steps.slice(0, index + 1), settings.rows, settings.keep]), 500);
  const before = JSON.stringify([setting.from, setting.steps.slice(0, index), settings.rows, settings.keep]);
  const preview = useKeyed(`${code}|${run}|${field.key}|${upTo}`, () =>
    getPreview(code, { settings: withSteps(settings, field.key, setting.steps.slice(0, index + 1)), run, limit: 1, field: field.key }),
  );
  const seen = useKeyed(step.kind === "map" ? `${code}|${run}|${field.key}|seen|${before}` : null, () =>
    getPreview(code, { settings: withSteps(settings, field.key, setting.steps.slice(0, index)), run, limit: 200 }),
  );
  const seenValues = seen.data
    ? valuesSeen(
        seen.data.rows.filter((row) => row.outcome !== "skipped").map((row) => row.value),
        field.key,
      )
    : null;
  return (
    <StepEditor
      step={step}
      onChange={onChange}
      onClose={onClose}
      disabled={disabled}
      preview={{ rows: preview.data?.field?.rows ?? [], loading: preview.busy, error: preview.error }}
    >
      {step.kind === "map" && (
        <ValueMap
          step={step}
          seen={seenValues}
          disabled={disabled}
          toValue={(text) => valueForType(text, field.type)}
          onChange={onChange}
        />
      )}
    </StepEditor>
  );
}

function FieldLine({
  code,
  field,
  label,
  settings,
  onChange,
  sample,
  kept,
  row,
  run,
  suggested,
  onConfirm,
  disabled,
  total,
}: {
  code: string;
  field: ImportFieldInfo;
  label: string;
  settings: Settings;
  onChange: (settings: Settings) => void;
  sample: Sample | undefined;
  kept: PreviewRow[];
  row: PreviewRow | undefined;
  run: string | null;
  suggested: boolean;
  onConfirm: () => void;
  disabled: boolean;
  total: number;
}) {
  const setting = settings.fields[field.key];
  const [open, setOpen] = useState<number | null>(null);
  const mapIndex = setting ? setting.steps.findIndex((step) => step.kind === "map") : -1;
  const beforeMap = setting && mapIndex >= 0 ? JSON.stringify([setting.from, setting.steps.slice(0, mapIndex), settings.rows, settings.keep]) : null;
  const settledBeforeMap = useDebounced(beforeMap, 800);
  const seen = useKeyed(settledBeforeMap ? `${code}|${run}|${field.key}|warn|${settledBeforeMap}` : null, () =>
    getPreview(code, { settings: withSteps(settings, field.key, setting.steps.slice(0, mapIndex)), run, limit: 200 }),
  );
  const unknown =
    mapIndex >= 0 && seen.data
      ? unmappedValues(
          setting.steps[mapIndex],
          valuesSeen(
            seen.data.rows.filter((one) => one.outcome !== "skipped").map((one) => one.value),
            field.key,
          ),
        )
      : [];

  const set = (next: FieldSetting | undefined) => {
    const fields = { ...settings.fields };
    if (next) fields[field.key] = next;
    else delete fields[field.key];
    onChange({ ...settings, fields });
  };
  const values = kept.map((one) => fieldValue(one.value, field.key));
  const count = values.filter((value) => !isEmptyValue(value)).length;
  const badRows = kept.map((one) => problemFor(one, field.label)).filter((one): one is string => Boolean(one));

  let problem: string | null = null;
  if (field.required && !isMapped(setting)) {
    problem = "Nothing is mapped yet. Pick a column, use a fixed value, or ask Scout to collect it.";
  } else if (isMapped(setting) && kept.length > 0 && count === 0) {
    problem = `Mapped, but empty in all ${kept.length.toLocaleString()} rows.`;
  } else if (badRows.length > 0) {
    problem = `${badRows.length.toLocaleString()} ${badRows.length === 1 ? "row" : "rows"} could not be read: ${[...new Set(badRows)].slice(0, 3).join("; ")}`;
  } else if (unknown.length > 0) {
    problem = `${unknown.length} ${unknown.length === 1 ? "value is" : "values are"} not in the map: ${unknown
      .slice(0, 3)
      .map((one) => `${one.value || "(empty)"} (${one.rows} ${one.rows === 1 ? "row" : "rows"})`)
      .join(", ")}`;
  }

  return (
    <FieldMapRow
      field={field}
      label={label}
      setting={setting}
      suggested={suggested}
      onConfirm={onConfirm}
      columns={sample?.columns ?? []}
      total={total}
      disabled={disabled}
      preview={row ? { value: fieldValue(row.value, field.key), problem: problemFor(row, field.label) } : null}
      filled={kept.length ? { count, of: kept.length } : null}
      problem={problem}
      onChange={set}
      steps={
        setting ? (
          <span style={{ display: "grid", gap: 4 }}>
            <StepChips
              steps={setting.steps}
              disabled={disabled}
              open={open}
              onOpen={setOpen}
              warnings={unknown.length && mapIndex >= 0 ? { [mapIndex]: `${unknown.length} new` } : {}}
              onChange={(steps) => set({ ...setting, steps })}
              renderEditor={(index, close) => (
                <StepEditorFor
                  code={code}
                  settings={settings}
                  field={field}
                  index={index}
                  run={run}
                  disabled={disabled}
                  onClose={close}
                  onChange={(step) => set({ ...setting, steps: setting.steps.map((one, i) => (i === index ? step : one)) })}
                />
              )}
            />
            {unknown.length > 0 && !disabled && (
              <button
                type="button"
                onClick={() => setOpen(mapIndex)}
                style={{ border: 0, background: "none", padding: 0, color: "var(--accent)", cursor: "pointer", textAlign: "left", fontSize: "var(--t-sm)" }}
              >
                Add to the map
              </button>
            )}
          </span>
        ) : null
      }
    />
  );
}

function AskScout({ code, field, disabled }: { code: string; field: ImportFieldInfo; disabled: boolean }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(
    `Please also collect "${field.label}" for every row${field.hint ? `: ${field.hint}` : "."}`,
  );
  const [state, setState] = useState<string | null>(null);
  return (
    <Popover
      open={open}
      onClose={() => setOpen(false)}
      trigger={
        <Button tone="subtle" disabled={disabled} onClick={() => setOpen(!open)}>
          Ask Scout
        </Button>
      }
    >
      <div style={{ display: "grid", gap: "var(--s2)", width: "min(26rem, 80vw)" }}>
        <p style={{ margin: 0 }}>A message for Scout’s agent. It may change the scraper and ask for your approval.</p>
        <Textarea rows={4} style={{ height: "auto" }} value={text} onChange={(event) => setText(event.target.value)} />
        {state && <p style={{ margin: 0, color: state.startsWith("Sent") ? "var(--good)" : "var(--bad)" }}>{state}</p>}
        <Button
          tone="primary"
          disabled={!text.trim() || state === "Sending…"}
          onClick={() => {
            setState("Sending…");
            sendChat(code, text.trim()).then(
              () => setState("Sent. Scout's answer shows in Needs answers."),
              (thrown) => setState(errorText(thrown, "Scout did not take the message")),
            );
          }}
        >
          Send to Scout
        </Button>
      </div>
    </Popover>
  );
}

export function FieldsStage({
  code,
  settings,
  onChange,
  fields,
  sample,
  preview,
  previewBusy,
  previewError,
  run,
  timeZone,
  suggested,
  onSuggested,
  disabled = false,
}: {
  code: string;
  settings: Settings;
  onChange: (settings: Settings) => void;
  fields: FieldsInfo;
  sample: Sample | undefined;
  preview: Preview | undefined;
  previewBusy: boolean;
  previewError?: string;
  run: string | null;
  timeZone?: string;
  suggested: string[];
  onSuggested: (keys: string[]) => void;
  disabled?: boolean;
}) {
  const [index, setIndex] = useState(0);
  const [suggesting, setSuggesting] = useState<string | null>(null);
  const kept = (preview?.rows ?? []).filter((row) => row.outcome !== "skipped");
  const shown = Math.min(index, Math.max(0, kept.length - 1));
  const row = kept[shown];
  const unmappedRequired = fields.fields.filter((field) => field.required && !field.many && !isMapped(settings.fields[field.key]));
  const total = kept.length || sample?.rowsCollected || 0;
  const groups = groupFields(fields.fields);
  const mappedCount = fields.fields.filter((field) => isMapped(settings.fields[field.key])).length;
  // A group opens by itself unless nothing in it is mapped or needed; a person's choice wins.
  const [shut, setShut] = useState<Record<string, boolean>>({});
  const isOpen = (key: string, count: { mapped: number; required: number }) =>
    shut[key] === undefined ? count.mapped > 0 || count.required > 0 : !shut[key];

  const suggest = () => {
    setSuggesting("Reading the run…");
    getSuggestions(code, { settings, run }).then(
      (found) => {
        const fieldsNow = { ...settings.fields };
        const added: string[] = [];
        for (const [key, setting] of Object.entries(found.fields)) {
          if (isMapped(fieldsNow[key])) continue;
          fieldsNow[key] = {
            from: { column: null, columns: [], value: null, template: null, ...setting.from, kind: setting.from.kind ?? "column" },
            steps: setting.steps ?? [],
          };
          added.push(key);
        }
        onChange({ ...settings, fields: fieldsNow });
        onSuggested([...new Set([...suggested, ...added])]);
        setSuggesting(
          added.length
            ? `Filled ${added.length} ${added.length === 1 ? "field" : "fields"} from the import's hints and the run's columns. Check each one and confirm it.`
            : "Nothing more to suggest. Every field that matched a column is already mapped.",
        );
      },
      (thrown) => setSuggesting(errorText(thrown, "Could not suggest")),
    );
  };

  return (
    <Column gap="sm">
      <Toolbar>
        <span>
          <strong>Fields for {fields.command}</strong>{" "}
          <span style={{ color: "var(--ink-2)", fontSize: "var(--t-sm)" }}>
            {preview ? `Sample: run of ${when(sample?.startedAt ?? null, timeZone)}` : "Reading the run…"}
            {previewBusy && preview ? " · updating…" : ""}
          </span>
        </span>
        <span style={{ flex: 1 }} />
        {kept.length > 0 && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--t-sm)", color: "var(--ink-2)" }}>
            <Button tone="subtle" icon aria-label="Row before" disabled={shown <= 0} onClick={() => setIndex(shown - 1)}>
              <ChevronLeft size={15} />
            </Button>
            Row {(shown + 1).toLocaleString()} of {kept.length.toLocaleString()}
            <Button tone="subtle" icon aria-label="Row after" disabled={shown >= kept.length - 1} onClick={() => setIndex(shown + 1)}>
              <ChevronRight size={15} />
            </Button>
          </span>
        )}
        <Button onClick={suggest} disabled={disabled || suggesting === "Reading the run…"}>
          <Sparkles size={15} aria-hidden />
          {suggesting === "Reading the run…" ? "Suggesting…" : "Suggest"}
        </Button>
        {suggested.length > 0 && (
          <Button tone="subtle" disabled={disabled} onClick={() => onSuggested([])}>
            Confirm all {suggested.length}
          </Button>
        )}
      </Toolbar>
      {suggesting && suggesting !== "Reading the run…" && <Banner tone="info">{suggesting}</Banner>}
      {previewError && (
        <Banner tone="danger" title="The preview did not load.">
          {previewError} Your changes are kept.
        </Banner>
      )}
      {preview && preview.rowsCollected > (preview.rows ?? []).length && (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
          Showing the first {(preview.rows ?? []).length.toLocaleString()} of {preview.rowsCollected.toLocaleString()} rows in previews. Try uses all of them.
        </p>
      )}
      <div className="fieldsSummary">
        <Inline>
          <strong>
            {mappedCount} of {fields.fields.length} fields mapped
          </strong>
          <Meter value={mappedCount} of={fields.fields.length} label={`${mappedCount} of ${fields.fields.length} fields mapped`} />
          {unmappedRequired.length > 0 ? (
            <TonePill tone="warning">
              {unmappedRequired.length} needed {unmappedRequired.length === 1 ? "field" : "fields"} not mapped
            </TonePill>
          ) : (
            <TonePill tone="good">Every needed field is mapped</TonePill>
          )}
          <span style={{ color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
            {groups.length} groups · needed fields first in each
          </span>
          <Button tone="subtle" onClick={() => setShut(Object.fromEntries(groups.map((group) => [group.key, false])))}>
            Open all
          </Button>
          <Button tone="subtle" onClick={() => setShut(Object.fromEntries(groups.map((group) => [group.key, true])))}>
            Close all
          </Button>
        </Inline>
      </div>
      <Table>
        <thead>
          <tr>
            <Th>Our field</Th>
            <Th>From</Th>
            <Th>Steps</Th>
            <Th>Preview (row {shown + 1})</Th>
            <Th num>Filled</Th>
          </tr>
        </thead>
        {groups.map((group) => {
          const count = groupCount(group, settings);
          const open = isOpen(group.key, count);
          return (
            <tbody key={group.key}>
              <FieldGroupRow
                title={group.title}
                count={count}
                list={group.list}
                open={open}
                columns={5}
                onToggle={() => setShut({ ...shut, [group.key]: open })}
              />
              {open &&
                group.fields.map((field) => (
                  <FieldLine
                    key={field.key}
                    code={code}
                    field={field}
                    label={labelInGroup(field.label, group)}
                    settings={settings}
                    onChange={onChange}
                    sample={sample}
                    kept={kept}
                    row={row}
                    run={run}
                    total={total}
                    disabled={disabled}
                    suggested={suggested.includes(field.key)}
                    onConfirm={() => onSuggested(suggested.filter((key) => key !== field.key))}
                  />
                ))}
            </tbody>
          );
        })}
      </Table>
      {unmappedRequired.length > 0 && (
        <Banner tone="warning" icon={<TriangleAlert size={16} aria-hidden />} title="Needed before you go on:">
          <span style={{ display: "grid", gap: "var(--s1)", marginTop: "var(--s1)" }}>
            {unmappedRequired.map((field) => (
              <span key={field.key} style={{ display: "inline-flex", alignItems: "center", gap: "var(--s2)", flexWrap: "wrap" }}>
                <strong>{field.label}</strong> — nothing Scout sends is mapped to it.
                <AskScout code={code} field={field} disabled={disabled} />
              </span>
            ))}
          </span>
        </Banner>
      )}
      {kept.length > 0 && row && (
        <p style={{ margin: 0, color: "var(--ink-3)", fontSize: "var(--t-sm)" }}>
          Filled is measured over {kept.length.toLocaleString()} kept rows. Row {shown + 1} key: {showValue(row.key)}.
        </p>
      )}
    </Column>
  );
}
